export interface InputCallbacks {
  onLeft: () => void;
  onRight: () => void;
  onDodgeLeft: () => void;
  onDodgeRight: () => void;
  onMoveAxis?: (axis: number) => void;
  onMoveVector?: (x: number, y: number) => void;
  onLookVector?: (x: number, y: number) => void;
  onJump: () => void;
  onSlide: () => void;
  onPause: () => void;
}

const DOUBLE_TAP_MS = 250;
const DEFAULT_GAMEPAD_DEADZONE = 0.22;

interface InputOptions {
  movementSensitivity?: number;
  cameraSensitivity?: number;
  controllerDeadzone?: number;
}

export class Input {
  private cleanup: (() => void)[] = [];
  private cb: InputCallbacks;
  private lastLeftAt = 0;
  private lastRightAt = 0;
  private leftHeld = false;
  private rightHeld = false;
  private forwardHeld = false;
  private backHeld = false;
  private virtualX = 0;
  private virtualY = 0;
  private rafId = 0;
  private gamepadJumpHeld = false;
  private gamepadSlideHeld = false;
  private gamepadPauseHeld = false;
  private smoothedMoveX = 0;
  private smoothedMoveY = 0;

  constructor(target: HTMLElement, cb: InputCallbacks, private options: InputOptions = {}) {
    this.cb = cb;

    const pumpAxis = () => {
      const movementSensitivity = this.options.movementSensitivity ?? 0.85;
      const cameraSensitivity = this.options.cameraSensitivity ?? 0.65;
      const controllerDeadzone = this.options.controllerDeadzone ?? DEFAULT_GAMEPAD_DEADZONE;
      const pad = readController(controllerDeadzone);
      const keyX = (this.leftHeld ? -1 : 0) + (this.rightHeld ? 1 : 0);
      const keyY = (this.forwardHeld ? 1 : 0) + (this.backHeld ? -1 : 0);
      const keyboardOrTouchX = this.virtualX + keyX;
      const keyboardOrTouchY = this.virtualY + keyY;
      const usePadMove = Math.hypot(pad.moveX, pad.moveY) > 0;
      if (usePadMove) {
        const shaped = shapeStick(pad.moveX, pad.moveY, movementSensitivity);
        this.smoothedMoveX += (shaped.x - this.smoothedMoveX) * 0.32;
        this.smoothedMoveY += (shaped.y - this.smoothedMoveY) * 0.32;
      } else {
        this.smoothedMoveX *= 0.62;
        this.smoothedMoveY *= 0.62;
        if (Math.hypot(this.smoothedMoveX, this.smoothedMoveY) < 0.01) {
          this.smoothedMoveX = 0;
          this.smoothedMoveY = 0;
        }
      }
      const axis = clamp(usePadMove ? this.smoothedMoveX : keyboardOrTouchX);
      const forward = clamp(usePadMove ? this.smoothedMoveY : keyboardOrTouchY);
      cb.onMoveAxis?.(axis);
      cb.onMoveVector?.(axis, forward);
      cb.onLookVector?.(pad.lookX * cameraSensitivity, pad.lookY * cameraSensitivity);
      if (pad.jump && !this.gamepadJumpHeld) cb.onJump();
      if (pad.slide && !this.gamepadSlideHeld) cb.onSlide();
      if (pad.pause && !this.gamepadPauseHeld) cb.onPause();
      this.gamepadJumpHeld = pad.jump;
      this.gamepadSlideHeld = pad.slide;
      this.gamepadPauseHeld = pad.pause;
      this.rafId = requestAnimationFrame(pumpAxis);
    };
    this.rafId = requestAnimationFrame(pumpAxis);
    this.cleanup.push(() => cancelAnimationFrame(this.rafId));

    const onKey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const now = performance.now();
      switch (e.key) {
        case "ArrowLeft":
        case "a":
        case "A": {
          this.leftHeld = true;
          e.preventDefault();
          if (now - this.lastLeftAt < DOUBLE_TAP_MS) {
            cb.onDodgeLeft();
            this.lastLeftAt = 0;
          } else {
            cb.onLeft();
            this.lastLeftAt = now;
          }
          break;
        }
        case "ArrowRight":
        case "d":
        case "D": {
          this.rightHeld = true;
          e.preventDefault();
          if (now - this.lastRightAt < DOUBLE_TAP_MS) {
            cb.onDodgeRight();
            this.lastRightAt = 0;
          } else {
            cb.onRight();
            this.lastRightAt = now;
          }
          break;
        }
        case "w":
        case "W":
        case "ArrowUp": {
          this.forwardHeld = true;
          e.preventDefault();
          break;
        }
        case " ":
          e.preventDefault();
          cb.onJump();
          break;
        case "ArrowDown":
        case "s":
        case "S":
          this.backHeld = true;
          e.preventDefault();
          break;
        case "Escape":
        case "p":
        case "P":
          cb.onPause();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    this.cleanup.push(() => window.removeEventListener("keydown", onKey));

    const onKeyUp = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowLeft":
        case "a":
        case "A":
          this.leftHeld = false;
          break;
        case "ArrowRight":
        case "d":
        case "D":
          this.rightHeld = false;
          break;
        case "w":
        case "W":
        case "ArrowUp":
          this.forwardHeld = false;
          break;
        case "ArrowDown":
        case "s":
        case "S":
          this.backHeld = false;
          break;
      }
    };
    window.addEventListener("keyup", onKeyUp);
    this.cleanup.push(() => window.removeEventListener("keyup", onKeyUp));
    void target;
  }

  setMoveVector(x: number, y: number) {
    this.virtualX = Math.max(-1, Math.min(1, x));
    this.virtualY = Math.max(-1, Math.min(1, y));
    this.cb.onMoveVector?.(this.virtualX, this.virtualY);
  }

  jump() {
    this.cb.onJump();
  }

  setOptions(options: InputOptions) {
    this.options = { ...this.options, ...options };
  }

  dispose() {
    for (const c of this.cleanup) c();
    this.cleanup = [];
  }
}

function readController(deadzone: number) {
  const pads = navigator.getGamepads?.() ?? [];
  const pad = Array.from(pads).find((p): p is Gamepad => !!p && p.connected);
  if (!pad) return { moveX: 0, moveY: 0, lookX: 0, lookY: 0, jump: false, slide: false, pause: false };

  // Standard/PS4 layout: left stick is axes 0/1; right stick is axes 2/3.
  // Some browser/OS PS4 mappings report the left-stick vertical axis on 3,
  // which made "back" move the camera. If axis 1 is quiet and axis 3 is the
  // only vertical signal, treat it as movement for this frame.
  const rawMoveX = pad.axes[0] ?? 0;
  let rawMoveY = pad.axes[1] ?? 0;
  const rawLookX = pad.axes[2] ?? 0;
  let rawLookY = pad.axes[3] ?? 0;
  if (Math.abs(rawMoveY) < deadzone && Math.abs(rawLookY) >= deadzone && Math.abs(rawLookX) < deadzone) {
    rawMoveY = rawLookY;
    rawLookY = 0;
  }
  const moveStick = applyDeadzone(rawMoveX, rawMoveY, deadzone);
  const lookStick = applyDeadzone(rawLookX, rawLookY, deadzone);
  const dpadX = (buttonDown(pad, 15) ? 1 : 0) - (buttonDown(pad, 14) ? 1 : 0);
  const dpadY = (buttonDown(pad, 12) ? 1 : 0) - (buttonDown(pad, 13) ? 1 : 0);
  const useDpad = Math.hypot(dpadX, dpadY) > 0;

  return {
    moveX: useDpad ? dpadX : moveStick.x,
    moveY: useDpad ? dpadY : moveStick.y,
    lookX: lookStick.x,
    lookY: lookStick.y,
    jump: buttonDown(pad, 0), // Cross / A
    slide: buttonDown(pad, 1), // Circle / B
    pause: buttonDown(pad, 9), // Options / Start
  };
}

function buttonDown(pad: Gamepad, index: number) {
  const b = pad.buttons[index];
  return !!b && (b.pressed || b.value > 0.5);
}

function applyDeadzone(x: number, y: number, deadzone: number) {
  const mag = Math.hypot(x, y);
  const dz = Math.max(0.05, Math.min(0.4, deadzone));
  if (mag < dz) return { x: 0, y: 0 };
  const normalized = Math.min(1, (mag - dz) / (1 - dz));
  const scale = normalized / mag;
  return { x: clamp(x * scale), y: clamp(-y * scale) };
}

function shapeStick(x: number, y: number, sensitivity: number) {
  const mag = Math.min(1, Math.hypot(x, y));
  if (mag <= 0.001) return { x: 0, y: 0 };
  const sens = Math.max(0.25, Math.min(1.5, sensitivity));
  const exponent = 1.35 / sens;
  const shapedMag = Math.pow(mag, exponent);
  const scale = shapedMag / mag;
  return { x: clamp(x * scale), y: clamp(y * scale) };
}

function clamp(value: number) {
  return Math.max(-1, Math.min(1, value));
}
